import { useMemo } from 'react';

import { Icon } from '../../../shared/components/Icon';
import { generateCover } from '../../../shared/placeholder/cover';
import type { PublicProject } from '../types';
import { Reveal, RevealGroup } from './Reveal';
import { StatusBadge } from './StatusBadge';
import { TagList } from './TagList';

export function Work({ projects }: { readonly projects: readonly PublicProject[] }) {
  if (projects.length === 0) {
    return (
      <Reveal>
        <div className="rounded-lg border border-dashed border-line px-6 py-11 text-center">
          <p className="m-0 mb-1.5 text-muted">No projects published yet.</p>
          <small className="text-xs text-faint">
            Add one from the admin panel to see it here.
          </small>
        </div>
      </Reveal>
    );
  }

  return (
    <RevealGroup>
      <ul className="m-0 grid list-none gap-2 p-0">
        {projects.map((project) => (
          <ProjectRow key={project.id} project={project} />
        ))}
      </ul>
    </RevealGroup>
  );
}

function ProjectRow({ project }: { readonly project: PublicProject }) {
  // A project without a cover renders generated artwork, never an empty box (§ C).
  const coverSrc = useMemo(
    () =>
      project.cover?.thumbnailUrl ??
      generateCover({ title: project.title, status: project.status }),
    [project.cover, project.title, project.status],
  );

  const isGenerated = project.cover === null;

  return (
    <Reveal as="li" className="entry-row group">
      <div className="mt-1 overflow-hidden rounded border border-line bg-well transition-colors duration-300 group-hover:border-accent-line">
        <img
          src={coverSrc}
          // Generated artwork is decorative: it conveys nothing the title does not
          // already say, so an empty alt is correct rather than lazy.
          alt={isGenerated ? '' : `${project.title} cover`}
          width={project.cover?.width ?? 480}
          height={project.cover?.height ?? 300}
          loading="lazy"
          className="block aspect-[16/10] size-full object-cover transition-transform duration-700 ease-reveal group-hover:scale-[1.07]"
        />
      </div>

      <div>
        <h3 className="entry-title">
          {project.externalUrl === null ? (
            project.title
          ) : (
            <a href={project.externalUrl} target="_blank" rel="noreferrer">
              {project.title}
              <Icon name="arrow" size={14} className="entry-arrow" />
            </a>
          )}
          <StatusBadge status={project.status} />
        </h3>

        <p className="mt-0 mb-3 text-[14.5px]">{project.description}</p>

        <TagList tags={project.tags} />
      </div>
    </Reveal>
  );
}
